import {
    AmuContractTerms,
    Contract,
    DiscountRate,
    PrsDeductionCap,
    PrsDeductionCapTypeEnum,
    RateCard,
    RoyaltyModelsEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { find } from 'lodash';
import {
    CalcType,
    autoCalculateFinalRate,
    calcMultiplierName,
    calculateRateCalcType,
    calculateRevCalcType,
    calculateServiceRevenue,
    getFullRateFor3PRate
} from 'src/components/RateCardDetail/ThirdPartyRates/ThirdPartyRatesAutoCalcUtil';
import { createPlanConfiguration } from 'src/components/RateCardDetail/mappers/createPlanConfiguration';
import {
    CorePlan,
    FreeTrialPlan,
    PaperPlanRateGroup,
    RateCardCoreRateGridColumnName,
    RateMultiplierDisplayName,
    RateMultiplierName
} from 'src/constants';
import {
    ASD_TIER_23_COLUMNS,
    CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP,
    CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP,
    CORE_PLAN_MAP,
    CPP_CORE_PLAN_MAP
} from 'src/constants/rateCards';
import { IMetadata } from 'src/types/IMetadata';
import {
    IFirstPartyCorePlan,
    IFirstPartyRateGridRowData,
    IFullRateGridRowData,
    IMultiplierType,
    IPlanNameToMetadataObj,
    IRatchetModelIdtoRatchetsObj,
    IRateCardCoreRateGridRowData,
    IRateCardValues,
    IThirdPartyData,
    IThirdPartyRateGridRowData
} from 'src/types/IRateCard';
import { getDerivedMultiplierName, hasNonEmptyNumberValue } from 'src/util/RateCards';

/*
 * Map getContractsById API response to data format needed for View rate card tabs
 */
export const mapContractToRateCardValues = (
    contract: Contract,
    contractTerms: AmuContractTerms,
    ratchetModelObj: IRatchetModelIdtoRatchetsObj,
    planNameToMetadataObj: IPlanNameToMetadataObj,
    corePlanList: IFirstPartyCorePlan[],
    firstPartyPlanList: Record<string, string | boolean | number>[],
    thirdPartyPlanList: Record<string, string | boolean | number>[],
    multiplierList: IMultiplierType[],
    discountPlanMap: Map<number, DiscountRate>,
    metadata: IMetadata,
    isEditMode: boolean
) => {
    const coreRatesRowData = [] as IRateCardCoreRateGridRowData[];
    const firstPartyRatesRowData = [] as IFirstPartyRateGridRowData[];
    const thirdPartyRatesRowData = [] as IThirdPartyRateGridRowData[];
    const fullRatesRowData = [] as IFullRateGridRowData[];

    const autoCalcRateMultiplierTypeId = find(multiplierList, {
        name: RateMultiplierName.AUTO_CALC
    })?.rateMultiplierTypeId;

    const noneRateMultiplierId = find(multiplierList, { name: RateMultiplierName.NONE })?.rateMultiplierTypeId;

    contract.territoryTerms?.forEach((territoryTerm) => {
        // Assign data at TerritoryTerm level to coreRateRow
        const coreRateRow = {} as IRateCardCoreRateGridRowData;
        coreRateRow[RateCardCoreRateGridColumnName.Territory] = territoryTerm.territoryCode;
        coreRateRow[RateCardCoreRateGridColumnName.SettlementCurrency] = territoryTerm.settlementCurrency;
        coreRateRow[RateCardCoreRateGridColumnName.CAE] =
            territoryTerm.contentAcquisitionEntity?.contentAcquisitionEntityCode || '';
        if (territoryTerm.rateCards && territoryTerm.rateCards.length > 0)
            coreRateRow[RateCardCoreRateGridColumnName.RatecardCurrency] = territoryTerm.rateCards[0].currency;

        // Get Floor PSM value in adjustmentModel if there is any and update in coreRateRow
        const floorPSMAdjustmentModel = territoryTerm.adjustmentModels?.find((adjustmentModel) =>
            adjustmentModel.adjustmentModel?.includes(`PSM_FLOOR_${territoryTerm.territoryCode}`)
        );
        if (floorPSMAdjustmentModel) {
            coreRateRow[RateCardCoreRateGridColumnName.BlendedPSMFloor] =
                floorPSMAdjustmentModel.adjustmentModelDefinitions?.[0].value || '';
        }

        // Process data at RateCard level
        territoryTerm.rateCards?.forEach((rateCard: RateCard) => {
            // Process data for PSM core plans in Core Rates tab
            if (
                CORE_PLAN_MAP.has(rateCard.paperPlanName as string) &&
                rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.PSM
            ) {
                // @ts-ignore
                coreRateRow[CORE_PLAN_MAP.get(rateCard.paperPlanName)] = rateCard.rate;

                // Check if there are tier 2 and 3 rates under the ASD Ratecard
                if (rateCard.paperPlanName === CorePlan.HAWKFIRE_HOME_ONLY_MONTHLY) {
                    assignTierRateFromRatchets(rateCard, ratchetModelObj, coreRateRow, ASD_TIER_23_COLUMNS);
                }
            }

            // Process data for CPP core plans in Core Rates tab
            if (
                CPP_CORE_PLAN_MAP.has(rateCard.paperPlanName as string) &&
                rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.CPP
            ) {
                // @ts-ignore
                coreRateRow[CPP_CORE_PLAN_MAP.get(rateCard.paperPlanName)] = rateCard.rate;
            }

            // Process data for all plans in Full Rates tab
            if (
                planNameToMetadataObj[rateCard.paperPlanName as string] &&
                rateCard.royaltyModel?.royaltyModelCode !== RoyaltyModelsEnum.REV_SHARE
            ) {
                const planMetaObj = planNameToMetadataObj[rateCard.paperPlanName as string];
                const fullRateRow: IFullRateGridRowData = {
                    territory: territoryTerm.territoryCode || '',
                    region: territoryTerm.region?.regionCode || '',
                    rateCardCurrency: rateCard.currency || '',
                    settlementCurrency: territoryTerm.settlementCurrency || '',
                    cae: territoryTerm.contentAcquisitionEntity?.contentAcquisitionEntityCode || '',
                    displayName: planMetaObj['displayName'].toString() ?? '',
                    paperPlanName: rateCard.paperPlanName ?? '',
                    rateGroup: planMetaObj['rateGroup'].toString() ?? '',
                    rateType: rateCard.royaltyModel?.royaltyModelCode || '',
                    rate: rateCard.rate?.toString() || '',
                    tier2Rate: '',
                    tier3Rate: '',
                    serviceRev: rateCard.revenueOverride?.revenueOverride?.toString() || ''
                };

                // Check if there are tier 2 and 3 rates under the Ratecard
                assignTierRateFromRatchets(rateCard, ratchetModelObj, fullRateRow, ['tier2Rate', 'tier3Rate']);

                fullRatesRowData.push(fullRateRow);
            }
        });

        coreRatesRowData.push(coreRateRow);
    });

    // Check whether 90 Day FT CPP or HD 90 Day FT CPP or Montana 90 Day FT CPP rate exists in Core Rates, then add it to 1P rates grid if not
    const is90DayFTCPPInCoreRates = coreRatesRowData.some((row) =>
        hasNonEmptyNumberValue(row[RateCardCoreRateGridColumnName.NinetyDayFTCPP])
    );
    const isHD90DayFTCPPInCoreRates = coreRatesRowData.some((row) =>
        hasNonEmptyNumberValue(row[RateCardCoreRateGridColumnName.HDNinetyDayFTCPP])
    );
    const isMontana90DayFTCPPInCoreRates = coreRatesRowData.some((row) =>
        hasNonEmptyNumberValue(row[RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP])
    );

    // Process data for 1P plans in 1P Discounted Rates tab
    for (const firstPartyPlan of firstPartyPlanList) {
        let firstPartyRateRow = {
            ...firstPartyPlan,
            corePlanId: find(corePlanList, { corePlanDisplayName: firstPartyPlan.corePlan as string })?.corePlanId,
            rateType: RoyaltyModelsEnum.PSM
        } as IFirstPartyRateGridRowData;

        if (
            isHD90DayFTCPPInCoreRates &&
            firstPartyRateRow.paperPlanName === CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT
        ) {
            continue;
        }

        if (is90DayFTCPPInCoreRates) {
            if (firstPartyRateRow.paperPlanName === CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL) continue;
            if (
                (!isHD90DayFTCPPInCoreRates &&
                    firstPartyRateRow.paperPlanName === CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT) ||
                [
                    FreeTrialPlan.HAWKFIRE_FAMILY_90DAY_FREE_TRIAL,
                    FreeTrialPlan.HAWKFIRE_KATANA_FAM_MONTHLY_90_DAY_FT
                ].includes(firstPartyRateRow.paperPlanName as FreeTrialPlan)
            ) {
                firstPartyRateRow = {
                    ...firstPartyRateRow,
                    corePlan:
                        find(corePlanList, { corePlanName: CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL })
                            ?.corePlanDisplayName || '',
                    corePlanId: find(corePlanList, { corePlanName: CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL })
                        ?.corePlanId,
                    rateType: RoyaltyModelsEnum.CPP
                };
            }
        }

        if (isMontana90DayFTCPPInCoreRates) {
            if (firstPartyRateRow.paperPlanName === CorePlan.AMU_AB_ALL_DEVICES_90DAY_FREE_TRIAL) continue;
            if (
                [FreeTrialPlan.AMU_AB_FAMILY_90DAY_FREE_TRIAL].includes(
                    firstPartyRateRow.paperPlanName as FreeTrialPlan
                )
            ) {
                firstPartyRateRow = {
                    ...firstPartyRateRow,
                    corePlan:
                        find(corePlanList, { corePlanName: CorePlan.AMU_AB_ALL_DEVICES_90DAY_FREE_TRIAL })
                            ?.corePlanDisplayName || '',
                    corePlanId: find(corePlanList, { corePlanName: CorePlan.AMU_AB_ALL_DEVICES_90DAY_FREE_TRIAL })
                        ?.corePlanId,
                    rateType: RoyaltyModelsEnum.CPP
                };
            }
        }

        // load in the corresponding saved data returned from GetDiscountRates response
        if (discountPlanMap.has(firstPartyPlan.paperPlanId as number)) {
            const discountRate = discountPlanMap.get(firstPartyPlan.paperPlanId as number);
            firstPartyRateRow = {
                ...firstPartyRateRow,
                corePlanId: discountRate?.corePlanId?.toString() || '',
                corePlan:
                    find(corePlanList, { corePlanId: discountRate?.corePlanId?.toString() || '' })
                        ?.corePlanDisplayName || '',
                multiplierTypeId: discountRate?.rateMultiplierTypeId as number,
                multiplierName:
                    find(multiplierList, { rateMultiplierTypeId: discountRate?.rateMultiplierTypeId })?.displayName ||
                    '',
                multiplierValue: discountRate?.multiplier?.toString() || '',
                rateType: discountRate?.multiplier === 1 ? RoyaltyModelsEnum.CPP : RoyaltyModelsEnum.PSM
            };
        }
        // auto populate multiplier in Edit mode when there is no saved multiplier data from GetDiscountRates response
        else if (isEditMode) {
            const derivedMultiplierName = getDerivedMultiplierName(
                firstPartyRateRow as Record<string, string | boolean | number>
            );
            firstPartyRateRow = {
                ...firstPartyRateRow,
                multiplierName: derivedMultiplierName || '',
                multiplierTypeId: find(multiplierList, { displayName: derivedMultiplierName })?.rateMultiplierTypeId,
                multiplierValue:
                    derivedMultiplierName === RateMultiplierDisplayName.NONE
                        ? 1
                        : //@ts-ignore
                          contractTerms.rateMultipliers[
                              find(multiplierList, { displayName: derivedMultiplierName })?.contractTermMultiplierKey ||
                                  ''
                          ]
            };
        }

        // @ts-ignore
        firstPartyRatesRowData.push(firstPartyRateRow);
    }

    // get corePlanPSM and rateCardCurrency for 3P plans from core plan rates, psmDiscCap and revDiscCap for 3P plans from contract terms
    for (const thirdPartyPlan of thirdPartyPlanList) {
        const thirdPartyRow = {
            ...thirdPartyPlan,
            corePlanId: find(corePlanList, { corePlanDisplayName: thirdPartyPlan.corePlan as string })?.corePlanId
        } as IThirdPartyRateGridRowData;

        const corePlanRowData = coreRatesRowData.find(
            (row) => row[RateCardCoreRateGridColumnName.Territory] === thirdPartyRow.territory
        );
        if (corePlanRowData) {
            const coreRatesColumnName = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(thirdPartyRow.corePlan);
            // @ts-ignore
            thirdPartyRow.corePlanPSM = corePlanRowData[coreRatesColumnName];
            thirdPartyRow['rateCardCurrency'] = corePlanRowData[RateCardCoreRateGridColumnName.RatecardCurrency] || '';

            if (contractTerms.thirdPartyDeductionCaps) {
                const overridePsmDiscountCap = contractTerms.thirdPartyDeductionCaps?.territoryOverrides?.find(
                    (territoryOverride) => territoryOverride.territory === thirdPartyRow.territory
                );
                const psmDiscountCap = overridePsmDiscountCap
                    ? overridePsmDiscountCap.deductionCap
                    : contractTerms.thirdPartyDeductionCaps?.globalDeductionCap;

                thirdPartyRow.psmDiscCap = psmDiscountCap?.toString() || '';
            }

            thirdPartyRow.revDiscCap = contractTerms.thirdPartyDeductionCaps?.carrierBilling?.toString() || '';

            const territoryTerm = find(contract.territoryTerms, { territoryCode: thirdPartyRow.territory });
            const rateCard = find(territoryTerm?.rateCards, { paperPlanName: thirdPartyRow.paperPlanName });

            if (discountPlanMap.has(thirdPartyRow['paperPlanId'] as number)) {
                // If there is an entry exists then it has been saved atleast once
                thirdPartyRow.rateType = RoyaltyModelsEnum.PSM;
                // @ts-ignore
                const discountRate = discountPlanMap.get(thirdPartyRow['paperPlanId'] as number);
                thirdPartyRow.corePlanId = discountRate?.corePlanId?.toString() || '';

                thirdPartyRow.corePlan =
                    find(corePlanList, { corePlanId: discountRate?.corePlanId?.toString() || '' })
                        ?.corePlanDisplayName || '';
                if (corePlanRowData) {
                    const coreRatesColumnName = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(thirdPartyRow.corePlan);
                    // @ts-ignore
                    thirdPartyRow.corePlanPSM = corePlanRowData[coreRatesColumnName];
                    thirdPartyRow.rateCardCurrency =
                        corePlanRowData[RateCardCoreRateGridColumnName.RatecardCurrency] || '';
                }

                thirdPartyRow.rateCalcType =
                    discountRate?.rateMultiplierTypeId === autoCalcRateMultiplierTypeId
                        ? CalcType.AutoCalc
                        : CalcType.Derived;

                thirdPartyRow.multiplierName =
                    find(multiplierList, { rateMultiplierTypeId: discountRate?.rateMultiplierTypeId })?.displayName ||
                    '';
                thirdPartyRow.multiplierTypeId = discountRate?.rateMultiplierTypeId;
                thirdPartyRow.multiplierValue = discountRate?.multiplier?.toString() || '';
                autoCalculateFinalRate(thirdPartyRow);

                if (discountRate?.revenueOverride !== undefined) {
                    thirdPartyRow.revCalcType = CalcType.User;
                    thirdPartyRow.serviceRev = discountRate.revenueOverride.toString();
                } else {
                    thirdPartyRow.revCalcType = CalcType.AutoCalc;
                    calculateServiceRevenue(thirdPartyRow);
                }
            } else if (rateCard) {
                // There is an existing entry but are user entry values
                thirdPartyRow.rateType = rateCard.royaltyModel?.royaltyModelCode || '';
                thirdPartyRow.finalRate = rateCard.rate?.toString() || '';
                thirdPartyRow.rateCalcType = CalcType.User;
                if (rateCard.revenueOverride) {
                    thirdPartyRow.revCalcType = CalcType.User;
                    thirdPartyRow.serviceRev = rateCard.revenueOverride?.revenueOverride?.toString() || '';
                } else {
                    thirdPartyRow.revCalcType = CalcType.AutoCalc;
                    calculateServiceRevenue(thirdPartyRow);
                }
            } else if (isEditMode) {
                // There is no existing data so calculating all the values
                thirdPartyRow.rateType = CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP.get(thirdPartyRow.corePlan) as string;
                calculateRateCalcType(thirdPartyRow);
                calcMultiplierName(thirdPartyRow);
                thirdPartyRow.multiplierTypeId = find(multiplierList, {
                    displayName: thirdPartyRow.multiplierName
                })?.rateMultiplierTypeId;
                thirdPartyRow.multiplierValue =
                    // @ts-ignore
                    contractTerms.rateMultipliers[
                        find(multiplierList, { displayName: thirdPartyRow.multiplierName })
                            ?.contractTermMultiplierKey || ''
                    ];
                autoCalculateFinalRate(thirdPartyRow);
                calculateRevCalcType(thirdPartyRow);
                calculateServiceRevenue(thirdPartyRow);
                fullRatesRowData.push(
                    getFullRateFor3PRate(
                        thirdPartyRow,
                        corePlanRowData,
                        metadata.territoryMapping,
                        metadata.regionMapping!
                    )
                );
            }
            if (thirdPartyRow.rateType) {
                // Plan is added to 3P if there is a discount rate or rate card or if is in edit mode.
                thirdPartyRatesRowData.push(thirdPartyRow);
            }
        }
    }

    const thirdPartyData = {
        noneRateMultiplierId: noneRateMultiplierId,
        autoCalcRateMultiplierTypeId: autoCalcRateMultiplierTypeId,
        thirdPartyRates: thirdPartyRatesRowData
    } as unknown as IThirdPartyData;

    const contractTermsCopy: AmuContractTerms = {
        ...contractTerms,
        prsDeductionCap: getPrsDeductionCap(contractTerms.prsDeductionCap)
    };

    return {
        contractName: contract.contractName,
        description: contract.description || '',
        isDeleted: contract.isDeleted || false,
        isDraft: contract.isDraft || false,
        serviceId: contract.serviceId,
        createdBy: contract.createdBy || '',
        creationDate: contract.creationDate?.toString() || '',
        changedBy: contract.changedBy || '',
        changedDate: contract.changedDate?.toString() || '',
        entityActiveIntervals: contract.entityActiveIntervals || [],
        coreRates: coreRatesRowData,
        contractTerms: contractTermsCopy,
        firstPartyRates: firstPartyRatesRowData,
        thirdPartyData: thirdPartyData,
        fullRates: fullRatesRowData,
        planConfiguration: createPlanConfiguration(contract, metadata.royaltyModelTypes ?? [])
    } as IRateCardValues;
};

const has1PPlanRate = (
    planNameToMetadataObj: IPlanNameToMetadataObj,
    rateCard: RateCard,
    firstPartyRatesPlanSet: Set<string>
) => {
    return (
        planNameToMetadataObj[rateCard.paperPlanName as string] &&
        !firstPartyRatesPlanSet.has(rateCard.paperPlanName as string) &&
        (([PaperPlanRateGroup.NonMonthly, PaperPlanRateGroup.FirstPartyPromo].includes(
            planNameToMetadataObj[rateCard.paperPlanName as string].rateGroup as PaperPlanRateGroup
        ) &&
            rateCard.royaltyModel?.royaltyModelCode !== RoyaltyModelsEnum.REV_SHARE) ||
            ([CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL, CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT].includes(
                rateCard.paperPlanName as CorePlan
            ) &&
                rateCard.royaltyModel?.royaltyModelCode === RoyaltyModelsEnum.PSM))
    );
};

const assignTierRateFromRatchets = (
    rateCard: RateCard,
    ratchetModelObj: IRatchetModelIdtoRatchetsObj,
    rateRowData: IRateCardCoreRateGridRowData | IFullRateGridRowData,
    appliedColumns: string[]
) => {
    if (!rateCard.ratchetModels || rateCard.ratchetModels.length === 0 || !rateCard.ratchetModels[0].ratchetModelId)
        return;

    // get the ratchetModels associated with the rateCard
    const ratchets = ratchetModelObj[rateCard.ratchetModels[0].ratchetModelId];

    // get the ratchets by filtering on rateCardId
    const filteredRatchets = ratchets.filter((ratchet) => ratchet.rateCardId === rateCard.rateCardId);

    // @ts-ignore
    if (filteredRatchets.length > 0) rateRowData[appliedColumns[0]] = filteredRatchets[0].rate?.toString() || '';
    // @ts-ignore
    if (filteredRatchets.length > 1) rateRowData[appliedColumns[1]] = filteredRatchets[1].rate?.toString() || '';
};

function getPrsDeductionCap(existing?: PrsDeductionCap): PrsDeductionCap {
    return {
        type: existing?.type ?? PrsDeductionCapTypeEnum.ROYALTY,
        value: existing?.value ?? 1
    };
}
